import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Settings, Megaphone, Plus, Trash2, ArrowUp, ArrowDown, Save, Link2, Store, Coins, RefreshCw } from 'lucide-react';
import { refreshStoreSettings } from '../lib/useStoreSettings';
import { useAdminAuth } from '../context/AdminAuthContext';
import { SeasonalEditor } from './SeasonalEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';

const MarqueePreview = ({ messages }) => (
  <div data-testid="marquee-preview" className="rounded-md overflow-hidden bg-gradient-to-r from-[hsl(var(--brand-strong))] via-[hsl(var(--brand))] to-[hsl(var(--brand-strong))] text-white">
    <div className="flex whitespace-nowrap animate-marquee py-2 text-[12.5px] font-medium tracking-wide">
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className="mx-8 inline-flex items-center gap-6">
          {messages.filter(m => m.text.trim()).map((m, j) => (
            <React.Fragment key={j}>
              {j > 0 && <span className="opacity-70">·</span>}
              <span className={m.link ? 'underline underline-offset-4 decoration-white/50' : ''}>{m.text}</span>
            </React.Fragment>
          ))}
        </span>
      ))}
    </div>
  </div>
);

const MarqueeEditor = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [enabled, setEnabled] = useState(true);
  const [messages, setMessages] = useState([]);
  const [pages, setPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState({ text: '', err: false });

  useEffect(() => {
    axios.get(`${API}/settings/marquee`)
      .then(({ data }) => { setEnabled(!!data.enabled); setMessages(data.messages || []); setLoaded(true); })
      .catch(() => setMsg({ text: 'Could not load current settings — refresh before editing.', err: true }));
    axios.get(`${API}/pages`).then(({ data }) => setPages(data || [])).catch(() => {});
  }, []);

  const upd = (i, k, v) => setMessages(prev => prev.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  const move = (i, d) => setMessages(prev => { const n = [...prev]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const remove = (i) => setMessages(prev => prev.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true); setMsg({ text: '', err: false });
    try {
      const { data } = await axios.put(`${API}/admin/settings/marquee`, { enabled, messages }, { headers });
      setMessages(data.messages); setEnabled(data.enabled);
      setMsg({ text: 'Announcement bar saved.', err: false });
    } catch (e) { setMsg({ text: e?.response?.data?.detail || 'Save failed', err: true }); }
    finally { setSaving(false); }
  };

  return (
    <section data-testid="marquee-editor" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="inline-flex items-center gap-2 text-[16px] font-semibold text-neutral-900 dark:text-neutral-100"><Megaphone size={16} className="text-emerald-600" /> Announcement bar</h3>
          <p className="text-[12.5px] text-neutral-500">The scrolling green bar under the website header.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
          <input data-testid="marquee-enabled-toggle" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
          Show bar on website
        </label>
      </div>

      {enabled && messages.some(m => m.text.trim()) && <MarqueePreview messages={messages} />}

      <datalist id="marquee-page-links">
        {pages.map(p => <option key={p.id} value={`/page/${p.slug}`}>{p.title}</option>)}
      </datalist>

      <div className="space-y-2">
        {messages.map((m, i) => (
          <div key={i} data-testid={`marquee-row-${i}`} className="flex items-center gap-2">
            <div className="flex flex-col">
              <button data-testid={`marquee-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-0.5 text-neutral-400 hover:text-neutral-800 disabled:opacity-30"><ArrowUp size={13} /></button>
              <button data-testid={`marquee-down-${i}`} disabled={i === messages.length - 1} onClick={() => move(i, 1)} className="p-0.5 text-neutral-400 hover:text-neutral-800 disabled:opacity-30"><ArrowDown size={13} /></button>
            </div>
            <input data-testid={`marquee-text-${i}`} className={inputCls + ' flex-[3]'} value={m.text} maxLength={120} placeholder="Message text" onChange={(e) => upd(i, 'text', e.target.value)} />
            <div className="relative flex-[2] min-w-0">
              <Link2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid={`marquee-link-${i}`} list="marquee-page-links" className={inputCls + ' pl-7 font-mono text-[12.5px]'} value={m.link} placeholder="Optional link: /page/... or https://" onChange={(e) => upd(i, 'link', e.target.value)} />
            </div>
            <button data-testid={`marquee-delete-${i}`} onClick={() => remove(i)} className="p-2 rounded-md text-neutral-500 hover:text-red-500"><Trash2 size={14} /></button>
          </div>
        ))}
        {messages.length === 0 && <p className="text-[13px] text-neutral-500">No messages yet.</p>}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
        <button data-testid="marquee-add-btn" disabled={messages.length >= 20} onClick={() => setMessages(prev => [...prev, { text: '', link: '' }])} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px] disabled:opacity-50"><Plus size={14} /> Add message</button>
        <div className="flex items-center gap-3">
          {msg.text && <span data-testid="marquee-msg" className={`text-[12.5px] ${msg.err ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</span>}
          <button data-testid="marquee-save-btn" onClick={save} disabled={saving || !loaded} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </section>
  );
};

const STORE_FIELDS = [
  { k: 'support_email', label: 'Support email *', ph: 'support@yourdomain.com', type: 'email' },
  { k: 'sender_name', label: 'Email sender name', ph: 'Healsglobe Exports' },
  { k: 'telegram', label: 'Telegram username', ph: 'yourhandle (without @)' },
  { k: 'whatsapp', label: 'WhatsApp number', ph: '+1 555 123 4567' },
  { k: 'phone', label: 'Phone', ph: '+1 555 123 4567' },
];

const StoreSettingsEditor = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', err: false });

  useEffect(() => {
    axios.get(`${API}/settings/store`).then(({ data }) => setForm(data))
      .catch(() => setMsg({ text: 'Could not load store settings — refresh before editing.', err: true }));
  }, []);

  const save = async () => {
    setSaving(true); setMsg({ text: '', err: false });
    try {
      const { data } = await axios.put(`${API}/admin/settings/store`, form, { headers });
      setForm(data); refreshStoreSettings();
      setMsg({ text: 'Store details saved.', err: false });
    } catch (e) { setMsg({ text: e?.response?.data?.detail || 'Save failed', err: true }); }
    finally { setSaving(false); }
  };

  return (
    <section data-testid="store-settings-editor" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-5 space-y-4">
      <div>
        <h3 className="inline-flex items-center gap-2 text-[16px] font-semibold text-neutral-900 dark:text-neutral-100"><Store size={16} className="text-emerald-600" /> Store contact details</h3>
        <p className="text-[12.5px] text-neutral-500">Shown in the website footer and checkout. The sender name and support email are also used in customer emails.</p>
      </div>
      {form && (
        <div className="grid sm:grid-cols-2 gap-3">
          {STORE_FIELDS.map(f => (
            <label key={f.k} className="block">
              <span className="text-[11px] tracking-widest uppercase text-neutral-500">{f.label}</span>
              <input data-testid={`store-${f.k}-input`} type={f.type || 'text'} className={inputCls} value={form[f.k] || ''} placeholder={f.ph}
                onChange={(e) => setForm(prev => ({ ...prev, [f.k]: e.target.value }))} />
            </label>
          ))}
          <label className="block sm:col-span-2">
            <span className="text-[11px] tracking-widest uppercase text-neutral-500">Footer tagline</span>
            <textarea data-testid="store-tagline-input" rows={2} maxLength={200} className={inputCls + ' resize-none'} value={form.tagline || ''}
              onChange={(e) => setForm(prev => ({ ...prev, tagline: e.target.value }))} />
          </label>
        </div>
      )}
      <div className="flex items-center justify-end gap-3">
        {msg.text && <span data-testid="store-msg" className={`text-[12.5px] ${msg.err ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</span>}
        <button data-testid="store-save-btn" onClick={save} disabled={saving || !form || !(form.support_email || '').trim()} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
      </div>
    </section>
  );
};

const CurrencyEditor = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [d, setD] = useState(null);
  const [markup, setMarkup] = useState('0');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState({ text: '', err: false });
  const apply = (data) => { setD(data); setMarkup(String(data.markup_pct ?? 0)); };
  useEffect(() => { axios.get(`${API}/admin/currency`, { headers }).then(({ data }) => apply(data)).catch(() => setMsg({ text: 'Could not load rates', err: true })); }, [headers]);
  const run = async (kind) => {
    setBusy(kind); setMsg({ text: '', err: false });
    try {
      const { data } = kind === 'refresh'
        ? await axios.post(`${API}/admin/currency/refresh`, {}, { headers })
        : await axios.put(`${API}/admin/currency`, { markup_pct: Number(markup) || 0 }, { headers });
      apply(data); setMsg({ text: kind === 'refresh' ? 'Rates refreshed.' : 'Markup saved.', err: false });
    } catch (e) { setMsg({ text: e?.response?.data?.detail || 'Failed', err: true }); }
    finally { setBusy(''); }
  };
  return (
    <section data-testid="currency-editor" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="inline-flex items-center gap-2 text-[16px] font-semibold text-neutral-900 dark:text-neutral-100"><Coins size={16} className="text-emerald-600" /> Currencies</h3>
          <p className="text-[12.5px] text-neutral-500">Prices are set in USD and converted with daily ECB rates (auto-updated every day). Converted prices round to whole numbers.</p>
        </div>
        <button data-testid="currency-refresh-btn" onClick={() => run('refresh')} disabled={!!busy} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px] disabled:opacity-60"><RefreshCw size={13} className={busy === 'refresh' ? 'animate-spin' : ''} /> Refresh now</button>
      </div>
      {d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="currency-rates">
            {d.currencies.map(c => (
              <div key={c.code} data-testid={`currency-rate-${c.code}`} className="rounded-lg border border-neutral-200 dark:border-neutral-800 px-3 py-2.5">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">{c.code}</div>
                <div className="text-[18px] font-semibold">{c.symbol}{Number(c.rate).toFixed(4)}</div>
                <div className="text-[11px] text-neutral-500">{c.code === 'USD' ? 'base' : `ECB ${Number(d.raw_rates?.[c.code] || 0).toFixed(4)}`}</div>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-neutral-500" data-testid="currency-updated">ECB date {d.date || '—'} · fetched {d.fetched_at ? new Date(d.fetched_at).toLocaleString() : '—'}</p>
          <div className="flex items-end gap-3 flex-wrap">
            <label className="block">
              <span className="text-[11px] tracking-widest uppercase text-neutral-500">Exchange buffer / markup %</span>
              <input data-testid="currency-markup-input" type="number" min="0" max="25" step="0.5" className={inputCls + ' w-32'} value={markup} onChange={(e) => setMarkup(e.target.value)} />
            </label>
            <button data-testid="currency-markup-save" onClick={() => run('markup')} disabled={!!busy} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> Save markup</button>
            {msg.text && <span data-testid="currency-msg" className={`text-[12.5px] ${msg.err ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</span>}
          </div>
        </>
      )}
    </section>
  );
};

const SiteSettingsPanel = () => (
  <div className="space-y-4" data-testid="site-settings-panel">
    <div>
      <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><Settings size={20} className="text-neutral-400" /> Site settings</h2>
      <p className="text-[12.5px] text-neutral-500">Storefront-wide settings.</p>
    </div>
    <StoreSettingsEditor />
    <SeasonalEditor />
    <CurrencyEditor />
    <MarqueeEditor />
  </div>
);

export default SiteSettingsPanel;
